import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../firebase';

const functions = getFunctions(app, 'europe-west1');

if (process.env.NODE_ENV === 'development') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch {
    // Already connected
  }
}

class XeroAuth {
  constructor() {
    this.getConfigFunction = httpsCallable(functions, 'getXeroOAuthConfig');
    this.exchangeCodeFunction = httpsCallable(functions, 'exchangeXeroCode');
    this.getStatusFunction = httpsCallable(functions, 'getXeroStatus');
    this.verifyFunction = httpsCallable(functions, 'verifyXeroConnection');
    this.refreshTokenFunction = httpsCallable(functions, 'refreshXeroToken');
  }

  generateSecureState() {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      const array = new Uint32Array(4);
      window.crypto.getRandomValues(array);
      return Array.from(array, (dec) => dec.toString(36)).join('');
    }
    return Math.random().toString(36).slice(2, 15) + Math.random().toString(36).slice(2, 15) + Date.now().toString(36);
  }

  async getAuthorizationUrl(state = null) {
    const configResult = await this.getConfigFunction();
    const config = configResult.data || {};
    const clientId = config.clientId || '';
    const redirectUri = config.redirectUri
      || (typeof window !== 'undefined' ? `${window.location.origin}/xero/callback` : 'https://app.svenry.com/xero/callback');
    const scope = config.scope
      || 'openid profile email offline_access accounting.settings.read accounting.contacts.read accounting.invoices.read accounting.attachments.read';
    const authorizeUrl = config.authorizeUrl || 'https://login.xero.com/identity/connect/authorize';

    if (!clientId) {
      throw new Error('Xero Client ID not configured. Add XERO_CLIENT_ID to Secret Manager.');
    }

    const stateValue = state || this.generateSecureState();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('xero_oauth_state', stateValue);
      sessionStorage.setItem('xero_oauth_redirect_uri', redirectUri);
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      scope,
      state: stateValue,
    });

    return `${authorizeUrl}?${params.toString()}`;
  }

  async exchangeCodeForToken(code, state) {
    const redirectUri = (typeof window !== 'undefined' && window.sessionStorage)
      ? sessionStorage.getItem('xero_oauth_redirect_uri')
      : null;
    const result = await this.exchangeCodeFunction({
      code,
      redirectUri: redirectUri || undefined,
      state,
    });
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('xero_oauth_redirect_uri');
    }
    return result.data;
  }

  async getConnectionStatus() {
    const result = await this.getStatusFunction();
    return result.data;
  }

  async verifyConnection() {
    const result = await this.verifyFunction();
    return result.data;
  }

  async refreshAccessToken() {
    const result = await this.refreshTokenFunction();
    return result.data;
  }

  async connect() {
    const state = this.generateSecureState();
    window.location.href = await this.getAuthorizationUrl(state);
  }
}

const xeroAuth = new XeroAuth();
export default xeroAuth;
